import { EventStreamCodec, type Message, type MessageHeaders } from '@smithy/eventstream-codec';
import { crc32 } from '@aws-crypto/crc32';
import { fromUtf8, toUtf8 } from '@smithy/util-utf8';
import type { TranscribeSessionResponse } from '@ampshift/shared';

/** AWS event-stream codec (binary framing used by Transcribe Streaming over WebSocket). */
export function createEventStreamCodec(): EventStreamCodec {
  return new EventStreamCodec(toUtf8, fromUtf8);
}

export interface DecodedEvent {
  messageType: string;
  eventType?: string;
  exceptionType?: string;
  contentType?: string;
  body: Uint8Array;
}

function headerString(headers: MessageHeaders, name: string): string | undefined {
  const h = headers[name];
  return h && h.type === 'string' ? h.value : undefined;
}

/** Encodes an `AudioEvent` carrying raw PCM bytes (empty body = end of audio). */
export function encodeAudioEvent(codec: EventStreamCodec, pcmBytes: Uint8Array): Uint8Array {
  return codec.encode({
    headers: {
      ':message-type': { type: 'string', value: 'event' },
      ':event-type': { type: 'string', value: 'AudioEvent' },
      ':content-type': { type: 'string', value: 'application/octet-stream' },
    },
    body: pcmBytes,
  });
}

/** Encodes an arbitrary JSON event (used by tests to emulate Transcribe's TranscriptEvent). */
export function encodeJsonEvent(codec: EventStreamCodec, eventType: string, payload: unknown, messageType = 'event'): Uint8Array {
  const headers: MessageHeaders = {
    ':message-type': { type: 'string', value: messageType },
    ':content-type': { type: 'string', value: 'application/json' },
  };
  if (messageType === 'exception') headers[':exception-type'] = { type: 'string', value: eventType };
  else headers[':event-type'] = { type: 'string', value: eventType };
  return codec.encode({ headers, body: fromUtf8(JSON.stringify(payload)) });
}

/**
 * Decodes a binary WebSocket frame. The message CRC is verified explicitly with @aws-crypto/crc32
 * before the codec parses headers, so a corrupted frame is rejected rather than misread.
 */
export function decodeEventStreamMessage(codec: EventStreamCodec, data: ArrayBuffer | Uint8Array): DecodedEvent {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
  if (bytes.byteLength < 16) throw new Error('Event-stream message too short');
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const expected = view.getUint32(bytes.byteLength - 4, false);
  const actual = crc32(bytes.subarray(0, bytes.byteLength - 4));
  if (expected !== actual) throw new Error('Event-stream message checksum mismatch');
  const message: Message = codec.decode(bytes);
  return {
    messageType: headerString(message.headers, ':message-type') ?? 'event',
    eventType: headerString(message.headers, ':event-type'),
    exceptionType: headerString(message.headers, ':exception-type'),
    contentType: headerString(message.headers, ':content-type'),
    body: message.body,
  };
}

export function int16ToBytes(pcm: Int16Array): Uint8Array {
  // Transcribe expects little-endian 16-bit PCM; Int16Array is platform-endian (little on all browsers we target).
  return new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.byteLength);
}

// ---------------------------------------------------------------- Transcribe result shapes
interface TranscribeItem {
  Content?: string;
  Confidence?: number;
  Type?: 'pronunciation' | 'punctuation';
}
interface TranscribeAlternative {
  Transcript?: string;
  Items?: TranscribeItem[];
}
interface TranscribeResult {
  ResultId?: string;
  IsPartial?: boolean;
  Alternatives?: TranscribeAlternative[];
  LanguageCode?: string;
  LanguageIdentification?: Array<{ LanguageCode?: string; Score?: number }>;
}
interface TranscriptEventBody {
  Transcript?: { Results?: TranscribeResult[] };
}

export interface TranscribeHandlers {
  /** Live text: all final segments so far plus the current partial. */
  onPartial(text: string): void;
  /** A segment became final. */
  onFinal(segmentText: string): void;
  onError(error: Error): void;
  onClose?(): void;
}

export interface TranscribeStreamResult {
  transcript: string;
  /** Mean item confidence over final items, 0..1; undefined when Transcribe sent none. */
  confidence?: number;
  languageCode?: string;
  identifiedLanguage?: string;
  /** From the first audio frame sent to socket close. */
  streamDurationMs: number;
  framesSent: number;
}

export interface TranscribeClientDeps {
  WebSocketCtor?: typeof WebSocket;
  now?: () => number;
  /** How long `end()` waits for trailing results after the empty AudioEvent. */
  drainTimeoutMs?: number;
}

export class TranscribeStreamClient {
  private ws: WebSocket | undefined;
  private readonly codec = createEventStreamCodec();
  private readonly now: () => number;
  private readonly WS: typeof WebSocket;
  private readonly drainTimeoutMs: number;
  private firstFrameAt: number | undefined;
  private closedAt: number | undefined;
  private framesSent = 0;
  private finalSegments: string[] = [];
  private partial = '';
  private confidenceSum = 0;
  private confidenceCount = 0;
  private languageCode: string | undefined;
  private identifiedLanguage: string | undefined;
  private closedPromise: Promise<void>;
  private resolveClosed: () => void = () => {};
  private failed: Error | undefined;

  constructor(
    private readonly session: TranscribeSessionResponse,
    private readonly handlers: TranscribeHandlers,
    deps: TranscribeClientDeps = {},
  ) {
    this.now = deps.now ?? (() => Date.now());
    this.WS = deps.WebSocketCtor ?? WebSocket;
    this.drainTimeoutMs = deps.drainTimeoutMs ?? 1500;
    this.languageCode = session.languageCode;
    this.closedPromise = new Promise<void>((resolve) => {
      this.resolveClosed = resolve;
    });
  }

  get isOpen(): boolean {
    return this.ws !== undefined && this.ws.readyState === this.WS.OPEN;
  }

  connect(timeoutMs = 10_000): Promise<void> {
    if (new Date(this.session.expiresAt).getTime() < this.now()) {
      return Promise.reject(new Error('Transcribe session URL has expired'));
    }
    return new Promise<void>((resolve, reject) => {
      let settled = false;
      const ws = new this.WS(this.session.websocketUrl);
      ws.binaryType = 'arraybuffer';
      this.ws = ws;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new Error('Timed out connecting to Transcribe'));
        this.abort();
      }, timeoutMs);
      ws.onopen = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve();
      };
      ws.onerror = () => {
        const err = new Error('Transcribe WebSocket error');
        this.failed = err;
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          reject(err);
        } else {
          this.handlers.onError(err);
        }
      };
      ws.onclose = () => {
        this.closedAt ??= this.now();
        this.resolveClosed();
        this.handlers.onClose?.();
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          reject(new Error('Transcribe WebSocket closed before opening'));
        }
      };
      ws.onmessage = (ev: MessageEvent<ArrayBuffer | string>) => this.onMessage(ev.data);
    });
  }

  /** Streams one PCM chunk. Silently dropped when the socket is not open (never buffered). */
  sendAudio(pcm: Int16Array): void {
    if (!this.isOpen || !this.ws) return;
    this.firstFrameAt ??= this.now();
    this.framesSent += 1;
    this.ws.send(encodeAudioEvent(this.codec, int16ToBytes(pcm)));
  }

  /** Signals end of audio, waits (≤ drainTimeoutMs) for the remaining results, then closes. */
  async end(): Promise<TranscribeStreamResult> {
    if (this.ws && this.isOpen) {
      try {
        this.ws.send(encodeAudioEvent(this.codec, new Uint8Array(0)));
      } catch {
        /* closing anyway */
      }
      await Promise.race([this.closedPromise, new Promise<void>((r) => setTimeout(r, this.drainTimeoutMs))]);
    }
    this.close();
    return this.result();
  }

  /** Closes immediately without waiting for results. */
  abort(): void {
    this.close();
  }

  result(): TranscribeStreamResult {
    const end = this.closedAt ?? this.now();
    const streamDurationMs = this.firstFrameAt === undefined ? 0 : Math.max(0, Math.round(end - this.firstFrameAt));
    const transcript = this.finalSegments.join(' ').replace(/\s+/g, ' ').trim();
    const out: TranscribeStreamResult = { transcript, streamDurationMs, framesSent: this.framesSent };
    if (this.confidenceCount > 0) out.confidence = Math.min(1, Math.max(0, this.confidenceSum / this.confidenceCount));
    if (this.languageCode) out.languageCode = this.languageCode;
    if (this.identifiedLanguage) out.identifiedLanguage = this.identifiedLanguage;
    return out;
  }

  private close(): void {
    const ws = this.ws;
    if (!ws) return;
    this.closedAt ??= this.now();
    if (ws.readyState === this.WS.OPEN || ws.readyState === this.WS.CONNECTING) {
      try {
        ws.close(1000, 'done');
      } catch {
        /* ignore */
      }
    }
    this.resolveClosed();
  }

  private onMessage(data: ArrayBuffer | string): void {
    if (typeof data === 'string') return; // Transcribe only sends binary frames
    let decoded: DecodedEvent;
    try {
      decoded = decodeEventStreamMessage(this.codec, data);
    } catch (err) {
      this.handlers.onError(err instanceof Error ? err : new Error(String(err)));
      return;
    }
    if (decoded.messageType === 'exception' || decoded.messageType === 'error') {
      let message = decoded.exceptionType ?? 'Transcribe error';
      try {
        const body = JSON.parse(toUtf8(decoded.body)) as { Message?: string; message?: string };
        message = `${decoded.exceptionType ?? 'TranscribeException'}: ${body.Message ?? body.message ?? ''}`.trim();
      } catch {
        /* body not JSON */
      }
      const err = new Error(message);
      this.failed = err;
      this.handlers.onError(err);
      return;
    }
    if (decoded.eventType !== 'TranscriptEvent') return;
    let body: TranscriptEventBody;
    try {
      body = JSON.parse(toUtf8(decoded.body)) as TranscriptEventBody;
    } catch {
      return;
    }
    for (const result of body.Transcript?.Results ?? []) this.applyResult(result);
  }

  private applyResult(result: TranscribeResult): void {
    const alt = result.Alternatives?.[0];
    const text = (alt?.Transcript ?? '').trim();
    if (result.LanguageCode) this.languageCode = result.LanguageCode;
    const best = result.LanguageIdentification?.slice().sort((a, b) => (b.Score ?? 0) - (a.Score ?? 0))[0];
    if (best?.LanguageCode) this.identifiedLanguage = best.LanguageCode;
    if (result.IsPartial === false) {
      this.partial = '';
      if (text) {
        this.finalSegments.push(text);
        for (const item of alt?.Items ?? []) {
          if (item.Type === 'pronunciation' && typeof item.Confidence === 'number') {
            this.confidenceSum += item.Confidence;
            this.confidenceCount += 1;
          }
        }
        this.handlers.onFinal(text);
      }
    } else {
      this.partial = text;
    }
    this.handlers.onPartial([...this.finalSegments, this.partial].filter(Boolean).join(' '));
  }

  get lastError(): Error | undefined {
    return this.failed;
  }
}
